import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Loader2, MapPin, Mail, Phone, CreditCard, Calendar, Package, AlertCircle, RotateCcw, Link2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { ordersAPI } from '../services/api';
import TrackingForm from './TrackingForm';
import TrackingEventsPanel from './TrackingEventsPanel';
import RefundToWalletPanel from './RefundToWalletPanel';
import SumupRefundPanel from './SumupRefundPanel';
import BillingAddress from './BillingAddress';
import ReviewReminderPanel from './ReviewReminderPanel';
import FittingCancelledNotice from './fitting/FittingCancelledNotice';
import InvoiceLangMenu from './InvoiceLangMenu';
import OrderVehicleChip from './OrderVehicleChip';
import { carrierLogo } from '../services/carriers';

export const ORDER_STATUSES = ['En attente', 'En cours', 'Expédié', 'Livré', 'Annulée', 'Remboursée'];
export const CLOSED_STATUSES = ['Annulée', 'Remboursée'];
export const PAYMENT_LABELS = { card: 'Carte bancaire', paypal: 'PayPal', apple: 'Apple Pay' };

export const getStatusColor = (status) => {
  switch (status) {
    case 'Livré': return 'bg-green-100 text-green-700';
    case 'Expédié': return 'bg-blue-100 text-blue-700';
    case 'En cours': return 'bg-yellow-100 text-yellow-700';
    case 'Annulée': return 'bg-red-100 text-red-700';
    case 'Remboursée': return 'bg-purple-100 text-purple-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const Info = ({ icon: Icon, label, children, testId }) => (
  <div className="flex gap-3" data-testid={testId}>
    <Icon size={16} className="text-orange-500 mt-0.5 flex-shrink-0" />
    <div className="text-sm min-w-0">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <div className="text-gray-800">{children}</div>
    </div>
  </div>
);

const OrderDetailDrawer = ({ orderId, onClose, onStatusChange, onOrderUpdated }) => {
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    setOrder(null);
    setError('');
    ordersAPI.getById(orderId).then(setOrder).catch((e) => setError(e.response?.data?.detail || 'Impossible de charger la commande'));
  }, [orderId]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const changeStatus = async (status) => {
    setUpdating(true);
    try {
      const ok = await onStatusChange(order.id, status);
      if (ok !== false) {
        if (status === 'Livré') { try { setOrder(await ordersAPI.getById(order.id)); return; } catch (_) { /* fallback below */ } }
        setOrder(o => ({ ...o, status, restocked: CLOSED_STATUSES.includes(status) }));
      }
    } finally {
      setUpdating(false);
    }
  };

  const ship = order?.shipping_info;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="order-detail-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        <div className="p-5 border-b bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white flex items-start justify-between">
          <div>
            <p className="text-xs text-blue-200 uppercase tracking-wide">Commande</p>
            <h2 className="text-xl font-bold" data-testid="order-detail-number">{order?.order_number || (order ? `#${order.id.slice(0, 8).toUpperCase()}` : '…')}</h2>
          </div>
          <button data-testid="order-detail-close" onClick={onClose} className="text-white/80 hover:text-white"><X size={22} /></button>
        </div>

        {error && (
          <p data-testid="order-detail-error" className="m-5 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2"><AlertCircle size={16} /> {error}</p>
        )}

        {!order && !error ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-orange-500" /></div>
        ) : order && (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-6">
              <div className="flex flex-wrap items-center gap-3">
                <span data-testid="order-detail-status" className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>{order.status}</span>
                {order.restocked && (
                  <span data-testid="order-detail-restocked" className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-1"><RotateCcw size={12} /> Stock restitué{order.cancelled_by === 'customer' ? ' · annulée par le client' : ''}</span>
                )}
                <select
                  data-testid="order-detail-status-select"
                  value={order.status}
                  disabled={updating}
                  onChange={(e) => changeStatus(e.target.value)}
                  className="text-sm border rounded px-2 py-1.5"
                >
                  {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <InvoiceLangMenu order={order} variant="button" label="Télécharger la facture" testId="order-detail-invoice" className="ml-auto" onDownload={(lang) => ordersAPI.downloadInvoice(order, lang).catch(() => setError('Échec du téléchargement de la facture'))} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                <Info icon={Calendar} label="Date" testId="order-detail-date">
                  {new Date(order.created_at).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </Info>
                <Info icon={CreditCard} label="Paiement" testId="order-detail-payment">{PAYMENT_LABELS[order.payment_method] || order.payment_method || '—'}</Info>
                <Info icon={Mail} label="E-mail" testId="order-detail-email">{ship?.email ? <a href={`mailto:${ship.email}`} className="text-blue-600 hover:underline">{ship.email}</a> : '—'}</Info>
                <Info icon={Phone} label="Téléphone" testId="order-detail-phone">{ship?.phone || '—'}</Info>
                <div className="sm:col-span-2">
                  <Info icon={MapPin} label="Adresse de livraison" testId="order-detail-address">
                    {ship ? (
                      <>
                        {ship.firstName} {ship.lastName}<br />
                        {ship.address}{ship.addressComplement ? `, ${ship.addressComplement}` : ''}<br />
                        {ship.postalCode} {ship.city}, {ship.country || 'France'}
                      </>
                    ) : order.customer_name}
                  </Info>
                </div>
              </div>

              <BillingAddress order={order} className="bg-gray-50 rounded-lg p-3" />

              <RefundToWalletPanel order={order} onDone={async () => { const fresh = await ordersAPI.getById(order.id); setOrder(fresh); onOrderUpdated && onOrderUpdated(fresh); }} />

              <SumupRefundPanel order={order} onDone={async () => { const fresh = await ordersAPI.getById(order.id); setOrder(fresh); onOrderUpdated && onOrderUpdated(fresh); }} />

              <TrackingForm order={order} onUpdated={(updated) => { setOrder(updated); onOrderUpdated && onOrderUpdated(updated); }} />

              <TrackingEventsPanel order={order} onUpdated={(updated) => { setOrder(updated); onOrderUpdated && onOrderUpdated(updated); }} />

              {order.payment_status === 'pending' && (
                <div className="border border-amber-300 bg-amber-50 rounded-lg p-4" data-testid="payment-link-panel">
                  <h3 className="font-semibold text-amber-900 flex items-center gap-2"><Link2 size={18} /> Commande par téléphone — paiement en attente</h3>
                  <p className="text-xs text-amber-800 mt-1">Créée par {order.created_by_admin || 'un administrateur'} en mode assistance. Le client doit régler via le lien reçu par e-mail (valable 7 jours). Ne pas expédier avant réception du paiement.</p>
                  <Button size="sm" variant="outline" className="mt-3 bg-white" data-testid="payment-link-resend" onClick={async () => { try { const r = await ordersAPI.resendPaymentLink(order.id); toast.success(r.message); } catch (e) { toast.error(e.response?.data?.detail || 'Envoi impossible'); } }}>Renvoyer un nouveau lien de paiement</Button>
                </div>
              )}
              {order.payment_status === 'paid' && order.created_by_admin && <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid="payment-link-paid">Commande par téléphone payée par le client le {new Date(order.paid_at).toLocaleString('fr-FR')}.</p>}
              <ReviewReminderPanel order={order} onDone={async () => { const fresh = await ordersAPI.getById(order.id); setOrder(fresh); onOrderUpdated && onOrderUpdated(fresh); }} />

              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><Package size={18} className="text-orange-500" /> Articles ({order.items.reduce((n, i) => n + i.quantity, 0)})</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm" data-testid="order-detail-items">
                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="px-3 py-2 text-left">Produit</th>
                        <th className="px-3 py-2 text-right">Qté</th>
                        <th className="px-3 py-2 text-right">PU TTC</th>
                        <th className="px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {order.items.map((item, idx) => (
                        <tr key={idx} data-testid={`order-detail-item-${idx}`}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-gray-50 rounded flex items-center justify-center flex-shrink-0">
                                {item.image ? <img src={item.image} alt="" className="max-w-full max-h-full object-contain" /> : <Package size={18} className="text-gray-300" />}
                              </div>
                              <div className="min-w-0">
                                <Link to={`/produit/${item.product_id}`} className="font-medium text-gray-900 hover:text-orange-600">{item.name}</Link>
                                <p className="text-xs text-gray-500">{item.brand}{item.ref ? ` · Réf. ${item.ref}` : ''}</p>
                                {item.pack_label && <span className="inline-block mt-0.5 text-[11px] font-medium bg-orange-50 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded" data-testid={`order-detail-pack-${idx}`}>{item.pack_label} · {item.units} u.</span>}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">{item.quantity}</td>
                          <td className="px-3 py-2 text-right">{item.price.toFixed(2)} €{item.discount_pct ? <span className="block text-[10px] text-green-700">−{item.discount_pct}% {item.discount_kind === 'pro' ? 'pro' : 'qté'}</span> : null}</td>
                          <td className="px-3 py-2 text-right font-medium">{(item.price * item.quantity).toFixed(2)} €</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="ml-auto w-full sm:w-64 space-y-1 text-sm" data-testid="order-detail-totals">
                <div className="flex justify-between"><span className="text-gray-500">Sous-total</span><span>{order.subtotal.toFixed(2)} €</span></div>
                {order.promo_discount > 0 && <div className="flex justify-between text-green-700" data-testid="order-promo-line"><span>Code promo {order.promo_code}{order.promo_label ? ` (${order.promo_label})` : ''}</span><span>-{order.promo_discount.toFixed(2)} €</span></div>}
                <div className="flex justify-between"><span className="text-gray-500 inline-flex items-center gap-1.5">Livraison{order.carrier_label ? <span className="text-gray-400 inline-flex items-center gap-1">{carrierLogo(order.carrier_label) && <img src={carrierLogo(order.carrier_label)} alt="" className="h-4 w-8 object-contain no-placeholder" data-testid="order-detail-carrier-logo" />}· {order.carrier_label}{order.shipping_delay ? ` (${order.shipping_delay})` : ''}</span> : null}</span><span data-testid="order-shipping-line">{order.shipping === 0 ? 'Gratuite' : `${order.shipping.toFixed(2)} €`}</span></div>
                {order.relay_point && <div className="text-xs text-gray-600 bg-orange-50 border border-orange-100 rounded px-2 py-1" data-testid="order-relay-point">Point relais : <strong>{order.relay_point.name}</strong> (n° {order.relay_point.id}) — {order.relay_point.address1}, {order.relay_point.postal_code} {order.relay_point.city}</div>}
                {order.fitting?.cancelled ? <FittingCancelledNotice order={order} compact admin onMessage={(text, type) => (type === 'error' ? toast.error(text) : toast.success(text))} onChanged={async () => { const fresh = await ordersAPI.getById(order.id); setOrder(fresh); onOrderUpdated && onOrderUpdated(fresh); }} /> : order.fitting && <div className={`text-xs rounded-md px-2 py-1.5 ${order.fitting.appointment ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`} data-testid="order-fitting-appointment">{order.fitting.appointment ? <>Rendez-vous de montage confirmé : <strong>{order.fitting.appointment.date_label}</strong>{order.fitting.appointment.note ? ` — ${order.fitting.appointment.note}` : ''}</> : <>Rendez-vous de montage : en attente de confirmation par {order.fitting.name}{order.fitting.phone ? ` (${order.fitting.phone})` : ''}</>}</div>}
                {order.fitting?.changes?.length > 0 && <ul className="text-[11px] text-gray-600 list-disc pl-4" data-testid="order-fitting-changes">{order.fitting.changes.map((ch, i) => <li key={i}>{new Date(ch.at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} — {ch.type === 'reassigned' ? `réaffecté ${ch.by === 'auto' ? 'automatiquement (centre muet 5 jours)' : "par l'admin"} de ${ch.from_centre} vers ${ch.to_centre}${ch.new_label ? ` (créneau ${ch.new_label} conservé)` : ''}` : ch.type === 'service_cancelled' ? `montage annulé par le client (avoir généré)${ch.old_label ? ` — créneau ${ch.old_label} annulé` : ''}` : ch.type === 'cancelled' ? `créneau ${ch.old_label} annulé par le client` : `déplacé par le client${ch.old_label ? ` de ${ch.old_label}` : ''} vers ${ch.new_label}`}</li>)}</ul>}
                {order.vehicle?.brand && <div className="pt-1"><OrderVehicleChip vehicle={order.vehicle} label="Véhicule" testId="order-vehicle" /></div>}
                {order.fitting && <div className="flex justify-between" data-testid="order-fitting-line"><span className="text-gray-500" title={`${order.fitting.address}, ${order.fitting.postcode} ${order.fitting.city} · ${order.fitting.phone}`}>Montage {order.fitting.name} ({order.fitting.city}) · {order.fitting.tyres} pneu{order.fitting.tyres > 1 ? 's' : ''}{order.fitting.cancelled ? ' — annulé, remboursé par avoir' : ''}</span><span className={order.fitting.cancelled ? 'line-through text-gray-400' : ''}>{order.fitting.total.toFixed(2)} €</span></div>}
                <div className="flex justify-between border-t pt-2 font-bold text-base"><span>Total TTC</span><span className="text-orange-600">{order.total.toFixed(2)} €</span></div>
                <p className="text-xs text-gray-400 text-right">dont TVA 20 % : {(order.total - order.total / 1.2).toFixed(2)} €</p>
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

export default OrderDetailDrawer;
